
import { useState } from 'react';

interface GameAreaProps {
  onCookieClick: () => void;
  score: number;
}

export default function GameArea({ onCookieClick, score }: GameAreaProps) {
  const [isPressed, setIsPressed] = useState(false);
  const [clickEffects, setClickEffects] = useState<Array<{ id: number; x: number; y: number }>>([]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onCookieClick();
    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 150);

    // Add click effect
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    
    setClickEffects(prev => [...prev, { id, x, y }]);
    setTimeout(() => {
      setClickEffects(prev => prev.filter(effect => effect.id !== id));
    }, 1000);
  };

  return (
    <section id="game" className="py-16">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-amber-800 mb-3">Click the Cookie!</h2>
            <p className="text-lg text-gray-600">Each click bakes one delicious cookie</p>
          </div>

          <div className="flex justify-center items-center min-h-[500px]">
            <button
              onClick={handleClick}
              onMouseDown={() => setIsPressed(true)}
              onMouseUp={() => setIsPressed(false)}
              onMouseLeave={() => setIsPressed(false)}
              className={`relative w-80 h-80 rounded-full bg-gradient-to-br from-amber-400 via-orange-400 to-amber-500 shadow-2xl hover:shadow-3xl transition-all duration-150 cursor-pointer border-8 border-amber-600 ${
                isPressed ? 'scale-90' : 'scale-100 hover:scale-105'
              }`}
            >
              <div className="absolute inset-0 flex items-center justify-center text-9xl">
                🍪
              </div>

              {/* Click effects */}
              {clickEffects.map((effect) => (
                <div
                  key={effect.id}
                  className="absolute text-3xl font-bold text-amber-600 animate-ping pointer-events-none"
                  style={{
                    left: `${effect.x}px`,
                    top: `${effect.y}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  +1
                </div>
              ))}
            </button>
          </div>

          {/* Stats Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center">
              <div className="text-3xl mb-2">⚡</div>
              <div className="text-2xl font-bold text-amber-600">{score}</div>
              <div className="text-sm text-gray-600 mt-1">Total Clicks</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center">
              <div className="text-3xl mb-2">🏆</div>
              <div className="text-2xl font-bold text-amber-600">{Math.floor(score / 10)}</div>
              <div className="text-sm text-gray-600 mt-1">Level</div>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center">
              <div className="text-3xl mb-2">🎯</div>
              <div className="text-2xl font-bold text-amber-600">{score > 0 ? '100%' : '0%'}</div>
              <div className="text-sm text-gray-600 mt-1">Accuracy</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
